import React, { useState, useEffect } from "react";
import axios from "axios";
import logger from "../utils/logger";
import { PublisherDto } from "../models/publisher-dto";
import { CrosswordSummaryDto } from "../models/crossword-summary-dto";
import "./CrosswordSelector.css";

interface CrosswordSelectorProps {
  onCrosswordSelected: (crosswordId: number) => void;
  excludeParticipated: boolean;
}

interface SortConfig {
  key: keyof CrosswordSummaryDto;
  direction: "asc" | "desc";
}

const CrosswordSelector: React.FC<CrosswordSelectorProps> = ({
  onCrosswordSelected,
  excludeParticipated,
}) => {
  const [publisher, setPublisher] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [setter, setSetter] = useState("");
  const [crosswords, setCrosswords] = useState<CrosswordSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [publisherData, setPublisherData] = useState<PublisherDto[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "publicationDate", 
    direction: "desc",
  });

  const fetchCrosswords = async (pub: string, type: string, set: string, exclude: boolean) => {
    try {
      const response = await axios.get<CrosswordSummaryDto[]>("/api/cross/search", {
        params: {
          publisher: pub,
          sourceType: type,
          setter: set,
          excludeParticipated: exclude,
        },
      });
      setCrosswords(response.data);
    } catch (error) {
      logger.error("Error fetching crosswords:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch when filters change
  useEffect(() => {
    void Promise.resolve().then(() =>
      fetchCrosswords(publisher, sourceType, setter, excludeParticipated)
    );
  }, [publisher, sourceType, setter, excludeParticipated]);

  useEffect(() => {
    axios
      .get("/api/cross/publishers")
      .then((response) => {
        setPublisherData(response.data);
      })
      .catch((error) => {
        logger.error("Error fetching publisher data:", error);
      });
  }, []);

  const filteredSetters = (publisher
    ? publisherData.find((p) => p.name === publisher)?.setters || []
    : Array.from(new Set(publisherData.flatMap((p) => p.setters)))).sort();

  const filteredSourceTypes = publisher
    ? publisherData.find((p) => p.name === publisher)?.sourceTypes || []
    : Array.from(new Set(publisherData.flatMap((p) => p.sourceTypes)));

  const sortedCrosswords = [...crosswords].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  const handleSort = (key: keyof CrosswordSummaryDto) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const columns = [
    { id: "publisher", key: "publisher", label: "Publisher" },
    { id: "sourceType", key: "sourceType", label: "Type" },
    { id: "setter", key: "setter", label: "Setter" },
    { id: "publicationDate", key: "publicationDate", label: "Date" },
  ] as const;

  return (
    <div>
      <h2>Select a Crossword</h2>
      <div className="select-group">
        <label>Publisher:</label>
        <select
          value={publisher}
          onChange={(e) => setPublisher(e.target.value)}
        >
          <option value="">Any</option>
          {publisherData.map((p) => (
            <option key={p.name} value={p.name}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      <div className="select-group">
        <label>Source Type:</label>
        <select
          value={sourceType}
          onChange={(e) => setSourceType(e.target.value)}
        >
          <option value="">Any</option>
          {filteredSourceTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>
      <div className="select-group">
        <label>Setter:</label>
        <select value={setter} onChange={(e) => setSetter(e.target.value)}>
          <option value="">Any</option>
          {filteredSetters.map((setter) => (
            <option key={setter} value={setter}>
              {setter}
            </option>
          ))}
        </select>
      </div>
      <div>
        <h3>Matching Puzzles: {crosswords.length}</h3>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="crosswords-table">
            <table>
              <thead>
                <tr>
                  {columns.map(({ id, key, label }) => (
                    <th
                      key={`header-${id}`}
                      onClick={() =>
                        handleSort(key as keyof CrosswordSummaryDto)
                      }
                      className={
                        sortConfig.key === key ? sortConfig.direction : ""
                      }
                    >
                      {label}{" "}
                      {sortConfig.key === key && (
                        <span>
                          {sortConfig.direction === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                  ))}
                  <th key="header-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedCrosswords.map((crossword) => (
                  <tr key={`row-${crossword.id}`}>
                    <td>{crossword.publisher}</td>
                    <td>{crossword.sourceType}</td>
                    <td>{crossword.setter}</td>
                    <td>
                      {new Date(crossword.publicationDate).toLocaleDateString()}
                    </td>
                    <td>
                      <button onClick={() => onCrosswordSelected(crossword.id)}>
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrosswordSelector;
